import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { GoPencil } from "react-icons/go";
import { FiCheck, FiX } from "react-icons/fi";
import { apiRequestBodyBuilder } from "../pages/config/tableHelper";

// 1. CENTRALIZED DESIGN CONFIGURATION
const CONFIG = {
  iconContainerSize: "w-9 h-9", // Size of the circular badge container
  iconSize: "w-5 h-5", // Size of the inner SVG metric icon
  textSize: "text-[14.5px]", // Text size of the metrics descriptions
  headerTextSize: "text-lg", // Size of the "Performance Insights" title
  headerIconSize: "w-6 h-6", // Size of the trending icon in the header
  cardPadding: "p-5", // Padding inside the overall card
  cardMargin: "my-3", // Vertical margin around the card
};

// Defined metric categories with unique IDs, keywords, and distinct color schemes
const METRIC_DEFINITIONS = [
  {
    id: "cost_per_conversion",
    keywords: [
      "cost per conversion",
      "cost/conv",
      "cost / conv",
      "cost_per_conversion",
      "cpa",
      "cost per acquisition",
    ],
    bgColor: "bg-cyan-50",
    textColor: "text-cyan-600",
    borderColor: "border-cyan-100",
    getIcon: (iconSizeClass) => (
      <svg
        className={iconSizeClass}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
    ),
  },
  {
    id: "conversion_rate",
    keywords: [
      "conversion rate",
      "conv. rate",
      "conv rate",
      "conversion_rate",
      "cvr",
    ],
    bgColor: "bg-teal-50",
    textColor: "text-teal-600",
    borderColor: "border-teal-100",
    getIcon: (iconSizeClass) => (
      <svg
        className={iconSizeClass}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2z"
        />
      </svg>
    ),
  },
  {
    id: "ctr",
    keywords: [
      "ctr",
      "click-through",
      "click through",
      "interaction rate",
      "clickthrough",
    ],
    bgColor: "bg-amber-50",
    textColor: "text-amber-600",
    borderColor: "border-amber-100",
    getIcon: (iconSizeClass) => (
      <svg
        className={iconSizeClass}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 7h.01M15 17h.01M9 17l6-10m-3 5a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    id: "cpc",
    keywords: [
      "cpc",
      "cost per click",
      "cost/click",
      "average cpc",
      "avg cpc",
      "avg. cpc",
      "avg cost",
      "average cost",
    ],
    bgColor: "bg-indigo-50",
    textColor: "text-indigo-600",
    borderColor: "border-indigo-100",
    getIcon: (iconSizeClass) => (
      <svg
        className={iconSizeClass}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
        />
      </svg>
    ),
  },
  {
    id: "roas",
    keywords: ["roas", "return on ad spend", "roi", "return on investment"],
    bgColor: "bg-emerald-50",
    textColor: "text-emerald-600",
    borderColor: "border-emerald-100",
    getIcon: (iconSizeClass) => (
      <svg
        className={iconSizeClass}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    id: "impressions",
    keywords: ["impression", "impr.", "impr", "cpm", "reach", "views"],
    bgColor: "bg-purple-50",
    textColor: "text-purple-600",
    borderColor: "border-purple-100",
    getIcon: (iconSizeClass) => (
      <svg
        className={iconSizeClass}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
    ),
  },
  {
    id: "clicks",
    keywords: ["click", "clicks", "interaction", "interactions"],
    bgColor: "bg-blue-50",
    textColor: "text-blue-600",
    borderColor: "border-blue-100",
    getIcon: (iconSizeClass) => (
      <svg
        className={iconSizeClass}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
        />
      </svg>
    ),
  },
  {
    id: "cost",
    keywords: ["cost", "spend", "spent", "budget", "amount spent"],
    bgColor: "bg-rose-50",
    textColor: "text-rose-600",
    borderColor: "border-rose-100",
    getIcon: (iconSizeClass) => (
      <svg
        className={iconSizeClass}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  {
    id: "conversions",
    keywords: [
      "conversion",
      "conversions",
      "lead",
      "leads",
      "purchase",
      "purchases",
    ],
    bgColor: "bg-pink-50",
    textColor: "text-pink-600",
    borderColor: "border-pink-100",
    getIcon: (iconSizeClass) => (
      <svg
        className={iconSizeClass}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
];

// Default Fallback metric structure (allowed to repeat)
const DEFAULT_METRIC = {
  id: "default",
  bgColor: "bg-gray-50",
  textColor: "text-gray-600",
  borderColor: "border-gray-200",
  getIcon: (iconSizeClass) => (
    <svg
      className={iconSizeClass}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
};

// Helper: Smart matching using Regex word boundaries where appropriate
const matchKeywordInText = (text, keyword) => {
  if (keyword.length <= 4 || /^[a-z0-9_.\-\/]+$/i.test(keyword)) {
    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`\\b${escaped}\\b`, "i");
    return regex.test(text);
  }
  return text.toLowerCase().includes(keyword.toLowerCase());
};

// Assigns unique icons to each sentence without repeating non-default metric icons
const processSentencesWithUniqueIcons = (sentences, iconSizeClass) => {
  const usedMetricIds = new Set();

  return sentences.map((sentence) => {
    const text = sentence.trim();
    if (!text) return null;

    let selectedMetric = null;

    // Evaluate matching metrics in priority order
    for (const metric of METRIC_DEFINITIONS) {
      if (usedMetricIds.has(metric.id)) continue; // Skip already used icons

      const isMatch = metric.keywords.some((kw) =>
        matchKeywordInText(text, kw)
      );

      if (isMatch) {
        selectedMetric = metric;
        usedMetricIds.add(metric.id); // Register icon as used
        break;
      }
    }

    // Fallback if no matching unused metric is available
    if (!selectedMetric) {
      selectedMetric = DEFAULT_METRIC;
    }

    return {
      sentence: text,
      bgColor: selectedMetric.bgColor,
      textColor: selectedMetric.textColor,
      borderColor: selectedMetric.borderColor,
      icon: selectedMetric.getIcon(iconSizeClass),
    };
  }).filter(Boolean);
};

function InsightSection({
  insightType,
  apiInsightType,
  onInsightChange,
  onEditingChange,
}) {
  const [insight, setInsight] = useState("");
  const [isEditingInsight, setIsEditingInsight] = useState(false);
  const [insightArray, setInsightArray] = useState([]);
  const [overallInsightId, setOverallInsightId] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchInsightData = async () => {
      try {
        setLoading(true);
        const reqBody = apiRequestBodyBuilder(null, {
          insightType: apiInsightType,
        });
        const response = await fetch(
          "https://api.confidanto.com/get-insights/overall-insights",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(reqBody),
          }
        );
        const result = await response.json();
        const text = result.insight || "";
        setInsight(text);
        setOverallInsightId(result.id || 0);
        if (onInsightChange) {
          onInsightChange(text);
        }
      } catch (error) {
        console.error(`Error fetching ${insightType} insights:`, error);
      } finally {
        setLoading(false);
      }
    };
    fetchInsightData();
  }, [apiInsightType, insightType, onInsightChange]);

  const updateInsight = async (newInsight) => {
    try {
      await axios.post(
        "https://api.confidanto.com/get-insights/update-overall-insights",
        {
          id: overallInsightId,
          ad_account_id: localStorage.getItem("ad_account_id"),
          insights_text: newInsight,
          insight_type: insightType,
        }
      );
      if (onInsightChange) {
        onInsightChange(newInsight);
      }
    } catch (error) {
      console.error(`Error updating ${insightType} insight:`, error);
    }
  };

  const handleEditClick = () => {
    const sentences = insight
      .split(/(?<=\.)\s+/)
      .map((s) => s.trim())
      .filter((s) => s !== "");
    setInsightArray(sentences);
    setIsEditingInsight(true);
    if (onEditingChange) {
      onEditingChange(true);
    }
  };

  const handleSentenceChange = (index, value) => {
    const newArray = [...insightArray];
    newArray[index] = value;
    setInsightArray(newArray);
  };

  const handleSaveInsight = () => {
    const newInsight = insightArray.join(" ");
    setInsight(newInsight);
    setIsEditingInsight(false);
    if (onEditingChange) {
      onEditingChange(false);
    }
    updateInsight(newInsight);
  };

  const handleCancel = () => {
    setIsEditingInsight(false);
    if (onEditingChange) {
      onEditingChange(false);
    }
  };

  // Compute processed sentences dynamically whenever insight text changes
  const styledSentences = useMemo(() => {
    if (!insight) return [];
    const sentences = insight
      .split(/(?<=\.)\s+/)
      .map((s) => s.trim())
      .filter((s) => s !== "");
    return processSentencesWithUniqueIcons(sentences, CONFIG.iconSize);
  }, [insight]);

  if (loading && !insight) {
    return null;
  }

  if (!insight) {
    return null;
  }

  return (
    <div
      className={`bg-white ${CONFIG.cardMargin} rounded-lg border border-gray-200 border-l-4 border-l-blue-600 ${CONFIG.cardPadding} text-sm text-gray-800 relative group print:break-inside-avoid print:bg-white print:border-none print:shadow-none print:px-0`}
    >
      {/* Floating Edit Button when not editing */}
      {!isEditingInsight && (
        <button
          onClick={handleEditClick}
          className="absolute -top-4 -right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-10 print:hidden"
          title="Edit Insights"
        >
          <GoPencil size={24} />
        </button>
      )}

      {/* Header with Title and Save/Cancel Actions */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 text-blue-600 font-semibold text-lg">
          <svg
            className={`${CONFIG.headerIconSize} text-blue-600`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
            />
          </svg>
          <span
            className={`font-semibold text-blue-600 ${CONFIG.headerTextSize}`}
          >
            Overall Insights
          </span>
        </div>

        {/* Save / Cancel buttons */}
        {isEditingInsight && (
          <div className="flex items-center gap-2 print:hidden">
            <button
              onClick={handleSaveInsight}
              className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-green-700 transition-colors shadow-sm"
            >
              <FiCheck size={14} /> Save
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center gap-1 bg-gray-500 text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-gray-600 transition-colors shadow-sm"
            >
              <FiX size={14} /> Cancel
            </button>
          </div>
        )}
      </div>

      {!isEditingInsight ? (
        /* Structured column layout with guaranteed unique icons */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
          {styledSentences.map((item, index) => {
            const formatted = item.sentence.replace(
              /(\d+(?:\.\d+)?%?)/g,
              "<strong>$1</strong>"
            );

            return (
              <div key={index} className="flex items-start gap-3">
                {/* Circular icon container with unique badge styling */}
                <div
                  className={`flex-shrink-0 ${CONFIG.iconContainerSize} rounded-full flex items-center justify-center border ${item.bgColor} ${item.textColor} ${item.borderColor}`}
                >
                  {item.icon}
                </div>
                <div
                  className={`leading-relaxed pt-1 text-[#4a4a4a] ${CONFIG.textSize}`}
                  dangerouslySetInnerHTML={{ __html: formatted }}
                />
              </div>
            );
          })}
        </div>
      ) : (
        /* Editing Layout */
        <div className="space-y-3">
          {insightArray.map((sentence, index) => (
            <div key={index} className="flex items-start gap-2">
              <textarea
                className="w-full p-2 border border-gray-300 rounded-md text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y leading-relaxed"
                rows={2}
                value={sentence}
                onChange={(e) => handleSentenceChange(index, e.target.value)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default InsightSection;